import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, UtensilsCrossed } from "lucide-react";
import { makeT } from "@/i18n/strings";
import { pick, txt } from "@/lib/format";
import { imgSrc, imgSrcSet } from "@/lib/media";
import { applySeo, loadFonts, themeVars } from "@/lib/theme";
import { analytics, clickZone } from "@/lib/analytics";
import { MenuHeader } from "./MenuHeader";
import { MenuFooter } from "./MenuFooter";
import { readLang, saveLang } from "./MenuStates";

const MenuChoice = ({ m, lang, base, track }) => {
  const name = pick(m, "name", lang);
  const desc = pick(m, "description", lang);
  return (
    <Link to={`${base}/menu/${m.slug}`} onClick={() => track && analytics.menuSelect(m.id)} data-testid={`menu-choice-${m.slug}`}
      className="wt-card-hover group block overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wt-primary)]"
      style={{ borderRadius: "calc(var(--wt-radius) + 8px)", background: "var(--wt-card)", border: "1px solid color-mix(in srgb, var(--wt-text) 8%, transparent)" }}>
      <div className="wt-img-zoom relative aspect-[16/10] overflow-hidden" style={{ background: "linear-gradient(135deg, var(--wt-surface), var(--wt-card))" }}>
        {m.image_url ? (
          <img src={imgSrc(m.image_url, "md")} srcSet={imgSrcSet(m.image_url)} sizes="(min-width:640px) 50vw, 100vw" alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <UtensilsCrossed className="absolute inset-0 m-auto h-10 w-10 opacity-25" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        {m.logo_url && <img src={imgSrc(m.logo_url, "sm")} alt="" data-testid={`menu-choice-logo-${m.slug}`} className="absolute bottom-4 start-4 h-14 w-auto max-w-[45%] rounded-xl bg-black/40 object-contain p-1.5 backdrop-blur-md" />}
      </div>
      <div className="flex items-center gap-4 p-5">
        <div className="min-w-0 flex-1 space-y-1">
          <h2 lang={name.lang} className="break-words text-xl font-extrabold tracking-tight sm:text-2xl" data-testid={`menu-choice-name-${m.slug}`}>{name.text}</h2>
          {desc.text && <p lang={desc.lang} className="line-clamp-2 text-sm text-[var(--wt-muted)]">{desc.text}</p>}
        </div>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-white transition-transform group-hover:scale-110" style={{ background: "var(--wt-primary)" }}>
          <ArrowRight className="h-5 w-5 rtl:rotate-180" />
        </span>
      </div>
    </Link>
  );
};

export default function Landing() {
  const { data, mode, base } = useOutletContext();
  const s = data.settings;
  const [lang, setLang] = useState(() => readLang() || s.locale.default_lang);
  const t = useMemo(() => makeT(lang), [lang]);
  const track = mode === "public";

  useLayoutEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  useEffect(() => {
    loadFonts([s.theme.font_en, s.theme.font_ar]);
    applySeo(s, lang);
  }, [s, lang]);
  useEffect(() => {
    if (!track) return undefined;
    analytics.start(lang);
    return () => analytics.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  const switchLang = (l) => {
    if (l === lang) return;
    setLang(l);
    saveLang(l);
    if (track) analytics.langSwitch(l);
  };

  return (
    <div className="wt-root" dir={lang === "ar" ? "rtl" : "ltr"} lang={lang} style={themeVars(s, lang)} data-testid="landing-root" onClickCapture={(e) => track && analytics.click(clickZone(e.target))}>
      {mode === "preview" && <div className="bg-amber-400 py-1 text-center text-xs font-bold text-black" data-testid="preview-banner">{t("pub.preview")}</div>}
      {s.announcement.enabled && txt(s.announcement, "text", lang) && (
        <div data-testid="announcement-bar" className="px-4 py-2 text-center text-xs font-semibold text-white sm:text-sm" style={{ background: "var(--wt-primary)" }}>{txt(s.announcement, "text", lang)}</div>
      )}
      <div className="sticky top-0 z-40">
        <MenuHeader s={s} lang={lang} t={t} onLang={switchLang} hideSearch />
      </div>
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <h1 className="text-3xl font-black leading-tight tracking-tight sm:text-5xl" data-testid="landing-title">{t("pub.chooseMenu")}</h1>
        <p className="mt-2 text-sm text-[var(--wt-muted)] sm:text-base">{t("pub.chooseMenuHint")}</p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="menu-choices">
          {data.menus.map((m) => <MenuChoice key={m.id} m={m} lang={lang} base={base} track={track} />)}
        </div>
      </main>
      <MenuFooter s={s} lang={lang} t={t} />
    </div>
  );
}
